/**
 * Ce qu'on peut faire au monde pendant qu'il tourne.
 *
 * Trois gestes, et ils sont le cœur du renversement : jusqu'ici on réglait des
 * curseurs qui recalculaient une prédiction ; ici on **modifie le monde**, et
 * la suite n'est plus prévue par personne. Un scénario n'est qu'un point de
 * départ ; ce qui vient après appartient à l'utilisateur.
 *
 * Toutes les fonctions sont pures et rendent un monde neuf : deux états ne
 * peuvent pas se marcher dessus, et rien n'est tiré au hasard — poser une
 * planète sur le même monde donne toujours la même planète (CLAUDE.md §5).
 */

import type { Entity, World } from "../core/world.js";
import { circularSpeed } from "../core/orbit.js";
import { quantity, valueIn } from "../core/units.js";
import { fr } from "../i18n/fr.js";

const AU = 1.496e11;
const EARTH_MASS = 5.972e24;
const EARTH_RADIUS = 6.371e6;

function distanceFrom(one: Entity, centre: Entity): number {
  const x = valueIn(one.position.x, "m") - valueIn(centre.position.x, "m");
  const y = valueIn(one.position.y, "m") - valueIn(centre.position.y, "m");
  return Math.hypot(x, y);
}

function heaviestOf(world: World): Entity | undefined {
  let heaviest: Entity | undefined;
  for (const one of world.entities) {
    if (heaviest === undefined || valueIn(one.mass, "kg") > valueIn(heaviest.mass, "kg")) {
      heaviest = one;
    }
  }
  return heaviest;
}

/**
 * Pose une planète sur une orbite circulaire, au-delà du corps le plus éloigné.
 *
 * Le rayon est déterminé par ce qui est déjà là, l'identifiant par le nombre de
 * corps : deux appels sur le même monde donnent la même planète, et l'ajout se
 * rejoue à l'identique.
 */
export function addPlanet(world: World): World {
  const centre = heaviestOf(world);
  if (centre === undefined) return world;

  let farthest = 0;
  for (const one of world.entities) farthest = Math.max(farthest, distanceFrom(one, centre));

  const radius = Math.max(AU, farthest * 1.6);
  let index = world.entities.length + 1;
  while (world.entities.some((one) => one.id === `ajout-${String(index)}`)) index += 1;
  const speed = valueIn(circularSpeed(centre.mass, quantity(radius, "m")), "m/s");

  const added: Entity = {
    id: `ajout-${String(index)}`,
    label: `${fr.worldPlanet} ${String(index)}`,
    kind: "planet",
    mass: quantity(EARTH_MASS, "kg"),
    radius: quantity(EARTH_RADIUS, "m"),
    position: { x: quantity(valueIn(centre.position.x, "m") + radius, "m"), y: centre.position.y },
    velocity: { x: centre.velocity.x, y: quantity(valueIn(centre.velocity.y, "m/s") + speed, "m/s") },
  };

  return { ...world, entities: [...world.entities, added] };
}

export function removeEntity(world: World, id: string): World {
  const entities = world.entities.filter((one) => one.id !== id);
  if (entities.length === world.entities.length) return world;
  return { ...world, entities };
}

/**
 * Change la masse d'un corps sans toucher à sa position ni à sa vitesse.
 *
 * C'est volontairement brutal : alourdir une planète en cours de route ne la
 * remet pas sur une orbite circulaire, et le système s'en trouve déséquilibré.
 * C'est précisément ce qu'on vient voir.
 */
export function setMass(world: World, id: string, mass: number): World {
  if (!(mass > 0) || !Number.isFinite(mass)) return world;
  return {
    ...world,
    entities: world.entities.map((one) =>
      one.id === id ? { ...one, mass: quantity(mass, "kg") } : one,
    ),
  };
}


/** Position en mètres et vitesse en mètres par seconde, dans le repère du monde. */
export interface BodyPlacement {
  readonly x: number;
  readonly y: number;
  readonly vx: number;
  readonly vy: number;
}

export function placeBody(world: World, kind: "planet" | "star", at: BodyPlacement): World {
  if (![at.x, at.y, at.vx, at.vy].every(Number.isFinite)) return world;
  let index = 1;
  const star = kind === "star";
  const label = star ? fr.worldStar : fr.worldPlanet;
  while (world.entities.some((one) => one.id === `libre-${String(index)}` || one.label === `${label} ${String(index)}`)) index += 1;
  const added: Entity = {
    id: `libre-${String(index)}`,
    label: `${star ? fr.worldStar : fr.worldPlanet} ${String(index)}`,
    kind,
    mass: quantity(star ? 1.989e30 : EARTH_MASS, "kg"),
    radius: quantity(star ? 6.957e8 : EARTH_RADIUS, "m"),
    position: { x: quantity(at.x, "m"), y: quantity(at.y, "m") },
    velocity: { x: quantity(at.vx, "m/s"), y: quantity(at.vy, "m/s") },
  };
  return { ...world, entities: [...world.entities, added] };
}

export function moveBody(world: World, id: string, x: number, y: number): World {
  if (![x, y].every(Number.isFinite)) return world;
  return { ...world, entities: world.entities.map((one) => one.id === id
    ? { ...one, position: { x: quantity(x, "m"), y: quantity(y, "m") } } : one) };
}

export function setVelocity(world: World, id: string, vx: number, vy: number): World {
  if (![vx, vy].every(Number.isFinite)) return world;
  return { ...world, entities: world.entities.map((one) => one.id === id
    ? { ...one, velocity: { x: quantity(vx, "m/s"), y: quantity(vy, "m/s") } } : one) };
}
